import { RadioGroup, useRadioGroupContext, useRadioGroupItemContext } from "@ark-ui/react";
import { isBrowser } from "@base/lib/utils";
import { mdiCheck } from "@mdi/js";
import Icon from "@mdi/react";
import { type VariantProps, cva } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

export const controlVariants = cva("inline-flex items-center justify-center border rounded-full flex-shrink-0", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-[18px] h-[18px]",
      lg: "w-5 h-5",
    },
    color: {
      primary: "",
      secondary: "",
    },
    disabled: {
      true: "bg-gray-10 text-gray-300 border-gray-200",
      false: "border-gray-500 bg-white text-white",
    },
  },
  compoundVariants: [
    {
      color: "primary",
      disabled: false,
      class: "[&:is([data-state='checked'])]:bg-primary-500 [&:is([data-state='checked'])]:border-none",
    },
    {
      color: "secondary",
      disabled: false,
      class: "[&:is([data-state='checked'])]:bg-blue-500 [&:is([data-state='checked'])]:border-none",
    },
  ],
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

interface controlVariantProps extends VariantProps<typeof controlVariants> {}
type Props = Omit<ComponentProps<typeof RadioGroup.Item>, "className"> & {
  label?: ReactNode;
};

const Item = ({ children, label, ...rest }: Props) => {
  return (
    <RadioGroup.Item {...rest} className="flex gap-2 items-center">
      <CheckedItemRenderer label={label} />
    </RadioGroup.Item>
  );
};

export default Item;

const CheckedItemRenderer = ({ label }: Pick<Props, "label">) => {
  const { getRootProps } = useRadioGroupContext();
  const rootId = getRootProps().id;

  const rootElem = (isBrowser && rootId && document.getElementById(rootId)) || null;

  const size = (rootElem?.dataset.size || "md") as controlVariantProps["size"];
  const color = (rootElem?.dataset.color || "primary") as "primary" | "secondary";

  const { checked, disabled } = useRadioGroupItemContext();

  return (
    <>
      <RadioGroup.ItemControl className={controlVariants({ size, color, disabled })}>
        {checked && <Icon path={mdiCheck} size={size === "lg" ? "16px" : size === "md" ? "14px" : "12px"} />}
      </RadioGroup.ItemControl>
      {label && <RadioGroup.ItemText>{label}</RadioGroup.ItemText>}
      <RadioGroup.ItemHiddenInput />
    </>
  );
};
